import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Box, CircularProgress,
} from "@mui/material";
import { FileText } from "lucide-react";

export default function CoverLetterModal({ open, onClose, onSubmit, loading, defaultCompanyName = "" }) {
  const [companyName, setCompanyName] = useState(defaultCompanyName);
  const [hiringManagerName, setHiringManagerName] = useState("");
  const [companyError, setCompanyError] = useState(false);

  useEffect(() => {
    if (open) setCompanyName(defaultCompanyName);
  }, [open, defaultCompanyName]);

  const handleSubmit = () => {
    if (!companyName.trim()) {
      setCompanyError(true);
      return;
    }
    onSubmit({
      companyName: companyName.trim(),
      hiringManagerName: hiringManagerName.trim(),
    });
  };

  const handleClose = () => {
    if (loading) return;
    setCompanyName(defaultCompanyName);
    setHiringManagerName("");
    setCompanyError(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", p: 0.5 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "10px",
            background: "linear-gradient(135deg,#2563eb,#7c3aed)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <FileText size={18} color="#fff" />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
              Cover Letter Details
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Personalise your letter with company info
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          label="Company Name *"
          placeholder="e.g. Google, Microsoft, Stripe"
          value={companyName}
          onChange={(e) => { setCompanyName(e.target.value); if (e.target.value.trim()) setCompanyError(false); }}
          disabled={loading}
          error={companyError}
          helperText={companyError ? "Company name is required" : ""}
          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
        <TextField
          fullWidth
          label="Hiring Manager / HR Name"
          placeholder="e.g. Sarah Johnson  (optional)"
          value={hiringManagerName}
          onChange={(e) => setHiringManagerName(e.target.value)}
          disabled={loading}
          helperText='Used in the "Dear …" salutation'
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ borderRadius: "8px", color: "#64748b", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
          sx={{
            borderRadius: "8px", fontWeight: 700, px: 2.5,
            background: "linear-gradient(135deg,#2563eb,#7c3aed)",
            "&:hover": { background: "linear-gradient(135deg,#1d4ed8,#6d28d9)" },
          }}
        >
          {loading ? "Generating…" : "Generate Cover Letter"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
